import { FC, ReactNode, useContext } from 'react';
import { Box, Breakpoint, css, useMediaQuery } from '@mui/system';
import { Button, MobileStepper, Typography } from '@mui/material';
import { white } from '@styles/colors';
import WelcomStepIcons from '../WelcomStepIcons/WelcomStepIcons';
import { KeyboardArrowLeft, KeyboardArrowRight } from '@mui/icons-material';
import theme from '@styles/theme';
import { authContext } from '@staffedupusa/shared-components';

type Step = {
  label: string;
  subtitle?: string;
  subtitle2?: string;
  content: ReactNode;
  nextLabel: string;
  disableNext?: boolean;
  modalMaxWidth?: Breakpoint;
  showLogo?: boolean;
};

type OnboardingStepContentProps = {
  activeStep: number;
  handleNext: () => void;
  handleBack: () => void;
  steps: Step[];
};

const OnboardingStepContent: FC<OnboardingStepContentProps> = ({
  activeStep,
  handleNext,
  handleBack,
  steps,
}) => {
  const {authData} = useContext(authContext)
  const isCompanyFromTemplate = Boolean(authData?.user?.company?.etemplate_id)
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));
  return (
    <>
      {steps[activeStep].content}
      {activeStep !== 4 && (
        <>
          {activeStep === 0 ? (
            <Box textAlign="center" mt={3}>
              <Button
                onClick={handleNext}
                variant="contained"
                sx={{
                  borderWidth: '1px',
                  borderStyle: 'solid',
                  borderColor: 'transparent',
                  padding: '5px 50px !important',
                  '&:hover': {
                    borderColor: 'primary.main',
                    backgroundColor: 'transparent',
                    color: 'primary.main',
                    boxShadow: 'none',
                  },
                }}
              >
                {steps[activeStep].nextLabel}
              </Button>
              <WelcomStepIcons />
            </Box>
          ) : (
            <MobileStepper
              variant="progress"
              steps={steps.length}
              position="static"
              activeStep={activeStep}
              sx={{
                flexWrap: 'wrap',
                mt: 3,
                '& span[role="progressbar"]': {
                  order: { xs: '-1', sm: 0 },
                  width: { xs: '100%', sm: '50%' },
                  marginY: { xs: 2, sm: 0 },
                },
              }}
              nextButton={
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleNext}
                  disabled={
                    steps[activeStep].disableNext !== undefined
                      ? steps[activeStep].disableNext
                      : activeStep === steps.length - 1
                  }
                  sx={{
                    borderWidth: '1px',
                    borderStyle: 'solid',
                    borderColor: 'transparent',
                    '&:hover': {
                      borderColor: 'primary.main',
                      backgroundColor: 'transparent',
                      color: 'primary.main',
                      boxShadow: 'none',
                    },
                  }}
                >
                  {isXs ? 'Next' : steps[activeStep].nextLabel}
                  <KeyboardArrowRight />
                </Button>
              }
              backButton={
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleBack}
                  disabled={activeStep === 0 || activeStep === 7 || (activeStep === 3 && isCompanyFromTemplate)}
                  sx={{
                    '&:hover': {
                      borderColor: 'primary.main',
                      backgroundColor: 'primary.main',
                      color: white,
                    },
                  }}
                >
                  <KeyboardArrowLeft />
                  Back
                </Button>
              }
            />
          )}
        </>
      )}
    </>
  );
};

export default OnboardingStepContent;
